import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import moment from "moment";
import { toast } from "react-toastify";

import usePostRequest from "../../hooks/usePostRequest";
import { API_ENDPOINTS } from "../../constant/_constant";
import { ApprovalSteps, RequestStatusBadge } from "./ChangeRequestCard";
import { moduleRefLabel, sectionLabel } from "./changeRequestConfig";
import "../../components/ChangeHistory/ChangeHistory.css";

const REASON_MIN = 3;
const REASON_MAX = 1000;

const Value = ({ value, className }) => (
  <td className={className}>
    {value === null || value === undefined || value === "" ? (
      <span className="ch-empty-value">-</span>
    ) : (
      value
    )}
  </td>
);

/* One section of the request, laid out like a change-history card. */
const DiffBlock = ({ entry, request }) => {
  const badge = {
    CREATED: ["ch-badge-created", "Added"],
    DELETED: ["ch-badge-deleted", "Removed"],
    MODIFIED: ["ch-badge-modified", "Modified"],
  }[entry.action] || ["ch-badge-modified", "Modified"];

  return (
    <div className="ch-card">
      <div className="ch-card-head">
        <div className="ch-actor-name" style={{ textTransform: "none" }}>
          {sectionLabel(request, entry.section)}
        </div>
        <div className="ch-head-right">
          {entry.entity_label && <span className="ch-entity">{entry.entity_label}</span>}
          <span className={`ch-badge ${badge[0]}`}>{badge[1]}</span>
        </div>
      </div>
      <table className="ch-table">
        <thead>
          <tr>
            <th className="ch-param">Field</th>
            <th className="ch-before">Previous value</th>
            <th className="ch-after">New value</th>
          </tr>
        </thead>
        <tbody>
          {(entry.changes || []).map((row, index) => (
            <tr key={`${row.field}-${index}`}>
              <td className="ch-param">{row.label}</td>
              <Value value={row.before} className="ch-before" />
              <Value value={row.after} className="ch-after" />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Request detail: requested changes (field / previous / new), approver
 * steps, and Approve / Reject for the reviewer whose turn it is.
 *
 * @param {string}   requestId
 * @param {Function} onHide
 * @param {Function} onDone  (message) — after approve / reject / cancel succeeds
 */
function ChangeRequestDetailModal({ requestId, onHide, onDone }) {
  const [request, setRequest] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [mode, setMode] = useState(null); /* null | "approve" | "reject" */
  const [reason, setReason] = useState("");

  const { isLoading: isLoadingDetail, postData: fetchSingle } = usePostRequest(
    API_ENDPOINTS.CHANGE_REQUEST_SINGLE,
  );
  const { isLoading: isApproving, postData: postApprove } = usePostRequest(
    API_ENDPOINTS.APPROVE_CHANGE_REQUEST,
  );
  const { isLoading: isRejecting, postData: postReject } = usePostRequest(
    API_ENDPOINTS.REJECT_CHANGE_REQUEST,
  );
  const { isLoading: isCancelling, postData: postCancel } = usePostRequest(
    API_ENDPOINTS.CANCEL_CHANGE_REQUEST,
  );

  const isBusy = isApproving || isRejecting || isCancelling;

  useEffect(() => {
    if (!requestId) {
      return;
    }
    setRequest(null);
    setLoadError(null);
    setMode(null);
    setReason("");
    fetchSingle(
      { id: requestId },
      (res) => setRequest(res?.data || null),
      (err) => setLoadError(err?.message || "Could not load this request."),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const handleError = (err) => {
    toast.error(err?.message || "Something went wrong.");
    if (err?.conflicts?.length) {
      setLoadError(
        `${err.message} Changed fields: ${err.conflicts.join(", ")}.`,
      );
    }
  };

  const approve = () =>
    postApprove({ id: request.id }, (res) => onDone(res?.message), handleError);

  const reject = () =>
    postReject(
      { id: request.id, reason: reason.trim() },
      (res) => onDone(res?.message),
      handleError,
    );

  const cancel = () =>
    postCancel({ id: request.id }, (res) => onDone(res?.message), handleError);

  const reasonValid = reason.trim().length >= REASON_MIN;

  return (
    <Modal show={Boolean(requestId)} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <div className="d-flex align-items-center gap-3">
          <Modal.Title className="fs-5">
            Change Request{request ? ` · ${moduleRefLabel(request)}` : ""}
          </Modal.Title>
          {request && <RequestStatusBadge status={request.status} />}
        </div>
      </Modal.Header>

      <Modal.Body style={{ minHeight: "50vh" }}>
        {isLoadingDetail && !request && (
          <div className="ch-state">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading request…
          </div>
        )}

        {loadError && <Alert variant="danger">{loadError}</Alert>}

        {request && (
          <>
            <div className="cr-detail-head">
              <div className="cr-detail-item">
                <div className="cr-detail-label">Requested by</div>
                <div className="cr-detail-value">
                  {request.requester?.name || request.requester?.username}
                </div>
              </div>
              <div className="cr-detail-item">
                <div className="cr-detail-label">Requested at</div>
                <div className="cr-detail-value">
                  {moment(request.requested_at).format("MM/DD/YYYY HH:mm")}
                </div>
              </div>
              <div className="cr-detail-item">
                <div className="cr-detail-label">
                  {request.module_type === "PRODUCT" ? "Related product" : "Related order"}
                </div>
                <div className="cr-detail-value">{request.module_ref}</div>
              </div>
              <div className="cr-detail-item" style={{ minWidth: 240 }}>
                <div className="cr-detail-label">Approvers</div>
                <ApprovalSteps steps={request.steps} />
              </div>
            </div>

            {request.status === "REJECTED" && (
              <div className="cr-reject-box">
                <strong>Rejected by {request.rejected_by?.username}:</strong>{" "}
                {request.reject_reason}
              </div>
            )}
            {request.status === "APPROVED" && (
              <Alert variant="success" className="py-2">
                Approved — the changes were applied to the{" "}
                {request.module_type === "PRODUCT" ? "product" : "order"}
                {request.applied_at
                  ? ` on ${moment(request.applied_at).format("MM/DD/YYYY HH:mm")}`
                  : ""}
                .
              </Alert>
            )}

            <div className="cr-section-title">Requested changes</div>
            <div className="ch-list">
              {(request.diff || []).map((entry, index) => (
                <DiffBlock key={`${entry.section}-${index}`} entry={entry} request={request} />
              ))}
            </div>

            {mode === "reject" && (
              <Form.Group className="mt-3">
                <Form.Label className="fw-semibold">
                  Reason for rejecting <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  maxLength={REASON_MAX}
                  value={reason}
                  autoFocus
                  placeholder="Tell the requester why these changes can't be applied"
                  onChange={(event) => setReason(event.target.value)}
                />
                <div className="text-muted small">
                  The requester is emailed this reason.
                </div>
              </Form.Group>
            )}

            {mode === "approve" && (
              <Alert variant="warning" className="mt-3 mb-0">
                {request.approve_applies_now
                  ? `Approve and apply these changes to ${moduleRefLabel(request)} now?`
                  : "Approve these changes? They are applied once the other approver has approved too."}
                {request.approve_applies_now &&
                  request.steps?.some((step) => step.status === "WAITING") && (
                    <div className="small mt-1">
                      As a super admin your approval is final — the remaining
                      approvals are skipped.
                    </div>
                  )}
              </Alert>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="justify-content-between">
        <div>
          {request?.can_cancel && mode === null && (
            <Button variant="outline-secondary" onClick={cancel} disabled={isBusy}>
              {isCancelling ? <Spinner animation="border" size="sm" /> : "Cancel request"}
            </Button>
          )}
        </div>

        <div className="d-flex gap-2">
          {request?.can_act && mode === null && (
            <>
              <Button variant="outline-danger" onClick={() => setMode("reject")} disabled={isBusy}>
                Reject
              </Button>
              <Button variant="success" onClick={() => setMode("approve")} disabled={isBusy}>
                Approve
              </Button>
            </>
          )}

          {mode === "reject" && (
            <>
              <Button variant="outline-secondary" onClick={() => setMode(null)} disabled={isBusy}>
                Back
              </Button>
              <Button variant="danger" onClick={reject} disabled={isBusy || !reasonValid}>
                {isRejecting ? <Spinner animation="border" size="sm" /> : "Reject request"}
              </Button>
            </>
          )}

          {mode === "approve" && (
            <>
              <Button variant="outline-secondary" onClick={() => setMode(null)} disabled={isBusy}>
                Back
              </Button>
              <Button variant="success" onClick={approve} disabled={isBusy}>
                {isApproving ? <Spinner animation="border" size="sm" /> : "Confirm approval"}
              </Button>
            </>
          )}

          {mode === null && (
            <Button variant="secondary" onClick={onHide}>
              Close
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default ChangeRequestDetailModal;
